import { z } from 'zod'

export const CRM_OPPORTUNITY_CONTRACT_VERSION = '1.0.0' as const

const optionalQueryString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional(),
)

export const crmOpportunityReadQuerySchema = z.object({
  orgId: optionalQueryString,
  org_id: optionalQueryString,
  limit: optionalQueryString,
  includeArchived: optionalQueryString,
  include_archived: optionalQueryString,
  stage: optionalQueryString,
  companyId: optionalQueryString,
  company_id: optionalQueryString,
  cursor: optionalQueryString,
}).passthrough()

export const CRM_OPPORTUNITY_STAGES = [
  'qualified',
  'proposal_sent',
  'negotiation',
  'verbal_yes',
  'won',
  'lost',
] as const

export const crmOpportunityStageSchema = z.enum(CRM_OPPORTUNITY_STAGES)

export const crmOpportunitySummarySchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  companyId: z.string().min(1),
  contactId: z.string().nullable(),
  leadId: z.string().nullable(),
  createdByUserId: z.string().min(1),
  ownerUserId: z.string().nullable(),
  name: z.string().min(1),
  stage: crmOpportunityStageSchema,
  amountCents: z.number().finite().nullable(),
  currency: z.string().min(1),
  probability: z.number().finite().nullable(),
  expectedClose: z.string().nullable(),
  lostReason: z.string().nullable(),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough()

export const crmOpportunityReadSideEffectsSchema = z.object({
  db1Written: z.boolean(),
  auditWritten: z.boolean(),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

export const crmOpportunityListResponseSchema = z.object({
  contract_version: z.literal(CRM_OPPORTUNITY_CONTRACT_VERSION),
  opportunities: z.array(crmOpportunitySummarySchema),
  sideEffects: crmOpportunityReadSideEffectsSchema,
}).passthrough()

export const crmOpportunityDetailResponseSchema = z.object({
  contract_version: z.literal(CRM_OPPORTUNITY_CONTRACT_VERSION),
  opportunity: crmOpportunitySummarySchema,
  sideEffects: crmOpportunityReadSideEffectsSchema,
}).passthrough()

export const crmOpportunityStageHistoryEntrySchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  opportunityId: z.string().min(1),
  fromStage: crmOpportunityStageSchema.nullable(),
  toStage: crmOpportunityStageSchema,
  reason: z.string().nullable(),
  actorUserId: z.string().min(1),
  changedAt: z.string(),
}).passthrough()

export const crmOpportunityStageHistoryResponseSchema = z.object({
  contract_version: z.literal(CRM_OPPORTUNITY_CONTRACT_VERSION),
  stageHistory: z.array(crmOpportunityStageHistoryEntrySchema),
  nextCursor: z.string().nullable(),
  sideEffects: crmOpportunityReadSideEffectsSchema,
}).passthrough()

export const CRM_OPPORTUNITY_ERROR_CODES = [
  'CR_VALIDATION_FAILED',
  'CR_PERMISSION_DENIED',
  'CR_AUTH_UNAVAILABLE',
  'CR_MEMBERSHIP_UNAVAILABLE',
  'CR_DOMAIN_UNAVAILABLE',
  'CR_IDEMPOTENCY_KEY_REUSED',
] as const

export const crmOpportunityErrorEnvelopeSchema = z.object({
  contract_version: z.string().min(1),
  error: z.object({
    code: z.enum(CRM_OPPORTUNITY_ERROR_CODES),
    message: z.string().min(1),
    reference: z.string().min(1),
    details: z.record(z.unknown()).optional(),
    request_id: z.string().optional(),
  }).passthrough(),
}).passthrough()

export type CrmOpportunityReadQuery = z.infer<typeof crmOpportunityReadQuerySchema>
export type CrmOpportunityStage = z.infer<typeof crmOpportunityStageSchema>
export type CrmOpportunitySummary = z.infer<typeof crmOpportunitySummarySchema>
export type CrmOpportunityReadSideEffects = z.infer<typeof crmOpportunityReadSideEffectsSchema>
export type CrmOpportunityListResponse = z.infer<typeof crmOpportunityListResponseSchema>
export type CrmOpportunityDetailResponse = z.infer<typeof crmOpportunityDetailResponseSchema>
export type CrmOpportunityStageHistoryEntry = z.infer<typeof crmOpportunityStageHistoryEntrySchema>
export type CrmOpportunityStageHistoryResponse = z.infer<typeof crmOpportunityStageHistoryResponseSchema>
export type CrmOpportunityErrorCode = (typeof CRM_OPPORTUNITY_ERROR_CODES)[number]
export type CrmOpportunityErrorEnvelope = z.infer<typeof crmOpportunityErrorEnvelopeSchema>
